<template>
  <div class="row main-content">
    <div class="col">
      <h1 class="page-title font-custom text-uppercase mt-3">
        {{ $t('title.card.list') }}
      </h1>

      <div class="card rounded-0 mb-4">
        <form class="card-body">
          <div class="row mb-2">
            <!-- Distributor -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.distributor') }} *
              </label>
              <select
                v-model="criterias.selectedDistributor"
                :disabled="isDistributorLocked"
                :class="status(v$.criterias.selectedDistributor)"
                name="distributor"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.card.distributor')"
                @change="updateComProducts()"
              >
                <option
                  value
                  selected
                  disabled
                  hidden
                >
                  {{ $t('label.card.distributor') }}
                </option>
                <option
                  v-for="distributor in distributors"
                  :key="distributor.id"
                  :value="distributor"
                >
                  {{ distributor }}
                </option>
              </select>
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedDistributor.required.$invalid">{{ $t('error.VALIDATION_MANDATORY_CRITERIA') }}</span>
              </p>
            </div>

            <!-- Produit commercial -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.commercialProduct') }}
              </label>
              <select
                v-model="criterias.selectedCommercialProduct"
                :class="status(v$.criterias.selectedCommercialProduct)"
                name="commercialProduct"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.card.commercialProduct')"
              >
                <option
                  value
                  selected
                  disabled
                  hidden
                >
                  {{ $t('label.card.commercialProduct') }}
                </option>
                <option
                  v-for="product in comProducts"
                  :key="product.id"
                  :value="product"
                >
                  {{ product }}
                </option>
              </select>
            </div>

            <!-- Contrat financeur -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.funderContract') }}
              </label>
              <input
                v-model="criterias.selectedFunderContract"
                :class="status(v$.criterias.selectedFunderContract)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.card.funderContract')"
              >
            </div>

            <!-- Service beneficiaire -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.beneficiaryService') }}
              </label>
              <input
                v-model="criterias.selectedBeneficiaryService"
                :class="status(v$.criterias.selectedBeneficiaryService)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.card.beneficiaryService')"
              >
            </div>
          </div>

          <div class="row mb-2">
            <!-- Identifiant carte -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.cardId') }}
              </label>
              <input
                v-model="criterias.selectedCardId"
                :class="status(v$.criterias.selectedCardId)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.card.cardId')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedCardId.numeric.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
                <span v-else-if="v$.criterias.selectedCardId.maxLength.$invalid">{{ $t('error.CARDS_CARD_ID_MAX_LENGTH') }}</span>
                <span v-else-if="v$.criterias.selectedCardId.minLength.$invalid">{{ $t('error.CARDS_CARD_ID_MIN_LENGTH') }}</span>
              </p>
            </div>

            <!-- Numero de carte -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.cardNum') }}
              </label>
              <span
                :title="'Champ alphanumérique. PAN masqué. Entrez les 6 premiers chiffres + au moins 1 ( * ) + les 4 derniers chiffres.'"
                data-bs-toggle="tooltip"
                data-bs-placement="left"
                class="label label-default ms-2"
              >
                <i class="fa fa-question-circle fa-xs" />
              </span>
              <input
                v-model="criterias.selectedCardNum"
                :class="status(v$.criterias.selectedCardNum)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.card.cardNum')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedCardNum.maxLength.$invalid">{{ $t('error.CARDS_PAN_MAX_LENGTH') }}</span>
                <span v-else-if="v$.criterias.selectedCardNum.minLength.$invalid">{{ $t('error.CARDS_PAN_MIN_LENGTH') }}</span>
                <span v-else-if="v$.criterias.selectedCardNum.panMask.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
              </p>
            </div>

            <!-- Date d'expiration -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.expirationDate') }}
              </label>
              <input
                v-model="criterias.selectedExpirationDate"
                :class="status(v$.criterias.selectedExpirationDate)"
                type="text"
                class="form-control rounded-0"
                :placeholder="'MMYY'"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedExpirationDate.expDate.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
              </p>
            </div>

            <!-- Numéro de séquence -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.sequenceNumber') }}
              </label>
              <input
                v-model="criterias.selectedSequenceNumber"
                :class="status(v$.criterias.selectedSequenceNumber)"
                type="text"
                class="form-control rounded-0"
                :placeholder="$t('label.card.sequenceNumber')"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedSequenceNumber.seqNum.$invalid || v$.criterias.selectedSequenceNumber.maxLength.$invalid">{{ $t('error.VALIDATION_FORMAT') }}</span>
              </p>
            </div>
          </div>

          <div class="row mb-2">
            <!-- Statut carte -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.cardStatus') }}
              </label>
              <select
                v-model="criterias.selectedCardStatus"
                name="cardStatus"
                class="form-control rounded-0 form-select"
                :data-placeholder="$t('label.card.cardStatus')"
              >
                <option
                  value
                  selected
                  disabled
                  hidden
                >
                  {{ $t('label.card.cardStatus') }}
                </option>
                <option
                  v-for="type in cardStatusEnum"
                  :key="type.id"
                  :value="type.value"
                >
                  {{ type.text }}
                </option>
              </select>
            </div>

            <!-- Date d'activation -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.activationDate') }}
              </label>
              <input
                v-model="criterias.selectedActivationDate"
                :class="status(v$.criterias.selectedActivationDate)"
                type="date"
                class="form-control rounded-0"
                placeholder="yyyy-MM-dd"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedActivationDate.$invalid">{{ $t('error.AUTHORIZATION_MANDATORY_CRITERIA') }}</span>
              </p>
            </div>

            <!-- Date de création -->
            <div class="col-12 col-sm-6 col-xl-3">
              <label class="control-label fw-bold mb-1">
                {{ $t('label.card.creationDate') }}
              </label>
              <input
                v-model="criterias.selectedCreationDate"
                :class="status(v$.criterias.selectedCreationDate)"
                type="date"
                class="form-control rounded-0"
                placeholder="yyyy-MM-dd"
              >
              <p
                v-if="validationError"
                class="error"
              >
                <span v-if="v$.criterias.selectedCreationDate.$invalid">{{ $t('error.AUTHORIZATION_MANDATORY_CRITERIA') }}</span>
              </p>
            </div>
            <!-- Submit -->
            <div class="col justify-content-end text-end criteria-button">
              <BaseButton
                type="button"
                class="btn btn-default btn-reset me-2"
                @click-base-button-event-emitter="reset()"
              >
                {{ $t('button.reset') }}
              </BaseButton>
              <BaseButton
                class="btn btn-primary btn-submit text-uppercase font-inverted"
                type="button"
                @click-base-button-event-emitter="search()"
              >
                <i class="fa fa-search" />
                {{ $t('button.search') }}
              </BaseButton>
            </div>
          </div>
        </form>
      </div>
      <div
        v-if="isProcessing"
        class="text-center"
      >
        <img
          src="@/assets/img/loader.gif"
          class="center-block"
        >
      </div>

      <div
        v-else-if="error"
        class="alert alert-warning text-center border-0 shadow-sm rounded-0"
      >
        {{ $t('error.'+`${responseErr}`) }}
      </div>
      <div v-if="$store.state.cards.cards.length">
        <div class="table-responsive">
          <table class="table table-hover table-sm table-striped border">
            <thead>
              <tr>
                <th>{{ $t('label.card.funderContract') }}</th>
                <th>{{ $t('label.card.beneficiaryService') }}</th>
                <th>{{ $t('label.card.cardId') }}</th>
                <th>{{ $t('label.card.cardInfo') }}</th>
                <th>{{ $t('label.card.status') }}</th>
                <th>{{ $t('label.card.activationDate') }}</th>
                <th>{{ $t('label.card.creationDate') }}</th>
                <th>{{ $t('label.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="card in displayedCards"
                :key="card.id"
                class="mb-3"
              >
                <td>{{ card.funderContractRef }}</td>
                <td>{{ card.beneficiaryServiceRef }}</td>
                <td>{{ card.cardId }}</td>
                <td>{{ card.cardInfo }}</td>
                <td>{{ card.cardStatus }}</td>
                <td>{{ $filters.dateYYYYMMDD(card.activationDate) }}</td>
                <td>{{ $filters.dateYYYYMMDD(card.creationDate) }}</td>
                <td>
                  <BaseButton
                    type="button"
                    class="btn btn-sm btn-outline-secondary border-secondary"
                    @click-base-button-event-emitter="showDetails(card)"
                  >
                    {{ $t('button.detail') }}
                  </BaseButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Pager
          :posts="$store.state.cards.cards"
          @page-update="displayedCards = $event;"
        />
      </div>
    </div>
  </div>
</template>

<script src="./CardList.js"></script>

<style src="./CardList.scss" scoped lang="scss"></style>
